import axios from "axios";
import type { IFuseOptions } from "fuse.js";

import { Module, ModuleOperation } from "src/api/modulesApi";
import { PillVariants } from "src/base-components/Pill";
import {
  LifecycleState,
  ModuleKind,
  ModuleOperationStatus,
} from "src/clients/flow-api";

type ApiErrorBody = { detail?: string; errors?: { detail?: string }[] };

// Pull a human-readable message from an API error, handling both the JSON:API
// envelope ({ errors: [{ detail }] }) returned by the module endpoints and
// FastAPI's plain ({ detail }) shape. Falls back when the error isn't an Axios
// error or carries no detail.
export const getApiErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const data = axios.isAxiosError(error)
    ? (error.response?.data as ApiErrorBody | undefined)
    : undefined;
  return data?.errors?.[0]?.detail ?? data?.detail ?? fallback;
};

export const getModuleKindDisplayName = (kind: ModuleKind): string =>
  kind
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export type SearchableModule = Module & {
  _workspaceName: string;
  _workspaceSlug: string;
  _orgName: string;
  _operationText: string;
};

export const enrichModulesForSearch = (
  modules: Module[],
  workspaceMap: Map<string, { name: string; orgId: string; slug: string }>,
  orgMap: Map<string, string>,
): SearchableModule[] =>
  modules.map((mod) => {
    const ws = workspaceMap.get(mod.workspaceId);
    const opStrings = [
      ...mod.activeOperations,
      ...(mod.latestResolvedOperation ? [mod.latestResolvedOperation] : []),
    ].map(
      (op) =>
        `${JSON.stringify(op.patch)}${op.tags ? ` ${JSON.stringify(op.tags)}` : ""}`,
    );
    return {
      ...mod,
      _workspaceName: ws?.name ?? "",
      _workspaceSlug: ws?.slug ?? "",
      _orgName: ws?.orgId ? (orgMap.get(ws.orgId) ?? "") : "",
      _operationText: opStrings.join(" "),
    };
  });

export const MODULE_SEARCH_OPTIONS: IFuseOptions<SearchableModule> = {
  keys: [
    "id",
    "workspaceId",
    "kind",
    "_workspaceName",
    "_workspaceSlug",
    "_orgName",
    "activeOperations.id",
    "activeOperations.status",
    "latestResolvedOperation.id",
    "latestResolvedOperation.status",
    "_operationText",
  ],
  threshold: 0.3,
  ignoreLocation: true,
};

export const getOperationStatusPillVariant = (
  status: ModuleOperationStatus,
): PillVariants => {
  switch (status) {
    case ModuleOperationStatus.COMPLETED:
      return "green";
    case ModuleOperationStatus.IN_PROGRESS:
    case ModuleOperationStatus.PENDING:
      return "yellow";
    case ModuleOperationStatus.FAILED:
      return "red";
    case ModuleOperationStatus.SCHEDULED:
    case ModuleOperationStatus.CANCELLED:
      return "gray";
  }
};

export const getModuleOperationStatus = (
  mod: Module,
): ModuleOperationStatus | null => {
  if (mod.latestResolvedOperation?.status === ModuleOperationStatus.FAILED) {
    return ModuleOperationStatus.FAILED;
  }
  if (mod.activeOperations.length > 0) {
    const oldest = mod.activeOperations.reduce((a, b) =>
      (a.createdAt ?? "") < (b.createdAt ?? "") ? a : b,
    );
    return oldest.status;
  }
  return mod.latestResolvedOperation?.status ?? null;
};

export const getOperationStatusLabel = (
  status: ModuleOperationStatus,
): string =>
  status
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");

export const OPERATION_STATUS_FILTER_OPTIONS = Object.values(
  ModuleOperationStatus,
)
  .filter((status) => status !== ModuleOperationStatus.CANCELLED)
  .map((status) => ({
    value: status,
    label: getOperationStatusLabel(status),
  }));

export const operationMatchesSearch = (
  op: ModuleOperation,
  lower: string,
): boolean =>
  op.id.toLowerCase().includes(lower) ||
  op.status.toLowerCase().includes(lower) ||
  JSON.stringify(op.patch).toLowerCase().includes(lower) ||
  (op.tags ? JSON.stringify(op.tags).toLowerCase().includes(lower) : false);

export const moduleHasOperationMatch = (
  mod: Module,
  searchTerm: string,
): boolean => {
  if (!searchTerm) return false;
  const lower = searchTerm.toLowerCase();
  return (
    mod.activeOperations.some((op) => operationMatchesSearch(op, lower)) ||
    (mod.latestResolvedOperation
      ? operationMatchesSearch(mod.latestResolvedOperation, lower)
      : false)
  );
};

export const truncateJson = (
  obj: Record<string, unknown>,
  maxLength = 120,
): string => {
  const str = JSON.stringify(obj);
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + "…";
};

// --- Lifecycle state machine -------------------------------------------------
// Mirror of the flow-api StackLifecycleStateMachine
// (services/flow-api/app/flow_api/domain/lifecycle.py). The backend is the source
// of truth; this lets the UI present only legal choices. Index in LIFECYCLE_ORDER
// is how far along the lifecycle a stack is.
export const LIFECYCLE_ORDER: readonly LifecycleState[] = [
  LifecycleState.ACTIVE,
  LifecycleState.DEACTIVATED,
  LifecycleState.DISABLED_PROTECTION,
  LifecycleState.DATA_DELETED,
  LifecycleState.DELETED,
];

const LIFECYCLE_TRANSITIONS: Record<LifecycleState, LifecycleState[]> = {
  [LifecycleState.ACTIVE]: [LifecycleState.DEACTIVATED],
  [LifecycleState.DEACTIVATED]: [
    LifecycleState.ACTIVE,
    LifecycleState.DISABLED_PROTECTION,
  ],
  [LifecycleState.DISABLED_PROTECTION]: [LifecycleState.DATA_DELETED],
  [LifecycleState.DATA_DELETED]: [LifecycleState.DELETED],
  [LifecycleState.DELETED]: [],
};

export const LIFECYCLE_LABEL: Record<LifecycleState, string> = {
  [LifecycleState.ACTIVE]: "Active",
  [LifecycleState.DEACTIVATED]: "Deactivated",
  [LifecycleState.DISABLED_PROTECTION]: "Disabled Protection",
  [LifecycleState.DATA_DELETED]: "Data Deleted",
  [LifecycleState.DELETED]: "Deleted",
};

export const LIFECYCLE_VARIANT: Record<LifecycleState, PillVariants> = {
  [LifecycleState.ACTIVE]: "green",
  [LifecycleState.DEACTIVATED]: "gray",
  [LifecycleState.DISABLED_PROTECTION]: "yellow",
  [LifecycleState.DATA_DELETED]: "red",
  [LifecycleState.DELETED]: "dark-gray",
};

export const isAtOrBeyond = (
  state: LifecycleState,
  target: LifecycleState,
): boolean => LIFECYCLE_ORDER.indexOf(state) >= LIFECYCLE_ORDER.indexOf(target);

// Reaching DISABLED_PROTECTION (or beyond) is a point of no return: the FSM has no
// path back to ACTIVE from there. Used to gate an extra confirmation step.
export const isIrreversible = (target: LifecycleState): boolean =>
  isAtOrBeyond(target, LifecycleState.DISABLED_PROTECTION);

export const allowedTransitions = (from: LifecycleState): LifecycleState[] =>
  LIFECYCLE_TRANSITIONS[from];

export type LifecycleActions =
  | { status: "in_flight"; desired: LifecycleState }
  | { status: "ready"; targets: LifecycleState[] };

// Given a stack's current (deployed) state, its desired state, and an optional
// floor (the workspace's state, for modules), return what the user may do:
// nothing while a transition is in flight, otherwise the legal hops that don't
// fall behind the floor, ordered along the lifecycle.
export const computeAvailableActions = ({
  current,
  desired,
  floor,
}: {
  current: LifecycleState;
  desired: LifecycleState;
  floor?: LifecycleState;
}): LifecycleActions => {
  if (current !== desired) {
    return { status: "in_flight", desired };
  }
  const targets = allowedTransitions(current)
    .filter((target) => floor === undefined || isAtOrBeyond(target, floor))
    .sort((a, b) => LIFECYCLE_ORDER.indexOf(a) - LIFECYCLE_ORDER.indexOf(b));
  return { status: "ready", targets };
};
